import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';

import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription
} from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { CookieService } from 'ngx-cookie';

import { AccountType } from '../../account/account-types.enum';
import { CompanySettings } from '../../settings/model/company-settings';
import { User } from '../../user-management/users/user';
import { UsersService } from '../../user-management/users/users.service';
import { PermissionLevel } from '../permission/permission';
import { PermissionService } from '../permission/permission.service';

import { Version } from '../version/version';
import * as fromCompanySettingsStore from '../../settings/store';
import { Store } from '@ngrx/store';
import { TokenService } from '../../authentication/token.service';
import { AccountFacade } from '../../account/store/facades/account-facade';
import { Account } from '../../account/model/account';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import {
  ReportingEndPointsResponse,
  ReportsService
} from '../reports/reports.service';
import { IntercomService } from '../intercom/intercom.service';
import { GoogleAnalyticsService } from '../analytics/google-analytics.service';
import { datadogLog } from '../utils/datadog';
import { LocaleUtils } from '../../../localeselect/locale';

@Injectable()
export class SessionService implements OnDestroy {
  public readonly minimumAppVersion: Version = new Version(5, 5, 0);
  public currentAppVersion: Version;
  public redirectTo: string;
  public sessionStarted$: Observable<boolean>;
  private ngUnsubscribe: Subject<Subscription> = new Subject();
  private sessionStarted: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private accountFacade: AccountFacade,
    private users: UsersService,
    private permissionService: PermissionService,
    private cookies: CookieService,
    private tokenService: TokenService,
    private store: Store<fromCompanySettingsStore.CompanyState>,
    private router: Router,
    private reportService: ReportsService,
    private intercom: IntercomService,
    private ga: GoogleAnalyticsService,
    private featureFlagService: FeatureFlagService
  ) {
    this.sessionStarted$ = this.sessionStarted.asObservable();
  }

  public isComingFromApp(): boolean {
    return this.currentAppVersion != null;
  }

  public createSession(token: string, trackSession = true): void {
    this.tokenService.token = token;
    let tracked = false;
    combineLatest(
      fromCompanySettingsStore.getSettingsFromStore(this.store),
      this.users.getUser(),
      this.accountFacade.getAccountInformationFromStore(),
      this.reportService.getReportEndPoints()
    )
      .pipe(
        map(
          ([company, user, account, endPoints]: [
            CompanySettings,
            User,
            Account,
            ReportingEndPointsResponse
          ]) => {
            this.setLanguageCode(company);
            this.clearCookies();
            this.setLegacyCookies(company, user, endPoints);

            // @ts-ignore
            if (typeof window.DD_RUM !== 'undefined') {
              // @ts-ignore
              window.DD_RUM.setUser({
                id: user.id,
                name: user.username,
                email: user.email
              });
            }

            // @ts-ignore
            if (typeof window.DD_LOGS !== 'undefined') {
              // @ts-ignore
              window.DD_LOGS.setUser({
                id: user.id,
                name: user.username,
                email: user.email
              });
            }

            if (account.requiresPasswordReset && user.type === 'admin') {
              this.router.navigate(['/reset/forced']);
            } else {
              this.permissionService.changePermissionLevel(
                user.permissionLevel
              );
              this.permissionService.userRights = user.roles;
              if (trackSession && !tracked) {
                tracked = true;
                this.ga.trackEvent(
                  'System',
                  'login',
                  `${
                    this.isComingFromApp() ? 'APP' : 'WEB'
                  }_${user.type.toUpperCase()}_${
                    PermissionLevel[user.permissionLevel]
                  }`
                );
              }

              return this.featureFlagService
                .isNewAccountEnabled()
                .subscribe(isEnabled => {
                  const accountUrl = isEnabled ? '/new-account' : '/account';

                  const url =
                    (account.pastDue ||
                      account.type === AccountType.SUSPENDED) &&
                    user.permissionLevel !== PermissionLevel.EMPLOYEE
                      ? [accountUrl]
                      : this.getRoute(user.permissionLevel);

                  return this.router
                    .navigate(url)
                    .then((navigationSuccess: boolean) => {
                      if (navigationSuccess) {
                        this.sessionStarted.next(true);
                        this.intercom.init();
                        this.updateGoogleTagManager(account, company, user);
                      }
                      // TODO maybe? if false trigger logout
                    });
                });
            }
          }
        )
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getLocale(): string {
    return this.cookies.get('localeSetting');
  }

  public setLoginFromAppLegacyCookies(version: string) {
    this.cookies.put('origin', 'restaurantbutler');
    this.cookies.put('appVersion', version);

    datadogLog('warn', 'set in app legacy cookies', {
      cookies: {
        origin: this.cookies.get('origin'),
        appVersion: this.cookies.get('appVersion')
      }
    });
  }

  private setLegacyCookies(
    company: CompanySettings,
    user: User,
    endPoints: ReportingEndPointsResponse
  ): void {
    this.cookies.put(
      'deleteRights',
      CompanySettings.canDeleteReceipts(company) + ''
    );
    const locale: string = this.cookies.get('locale');
    if (!locale) {
      this.cookies.put('locale', 'en-US');
    }
    this.cookies.put('company', company.name);
    this.cookies.put('country', company.country, { storeUnencoded: true });
    this.cookies.put('openingstime', company.openingTime + '');
    this.cookies.put('vatInclusive', company.vatInclusive + '');
    this.cookies.put('enforceStock', company.enforceStock + '');
    this.cookies.put('localeSetting', company.locale);
    this.cookies.put('timeZoneID', company.timeZoneId);
    this.cookies.put('email', user.email);
    this.cookies.put('usrType', user.type);
    this.cookies.put('reportingPosServerUrl', endPoints.reportingPosServerUrl);
    this.cookies.put('blackbox', (company.type === 'blackbox restaurant') + '');
    if (this.isComingFromApp()) {
      this.cookies.put('origin', 'restaurantbutler');
      this.cookies.put('appVersion', this.currentAppVersion.toString());
    }
  }

  private getRoute(permissionLevel: PermissionLevel): Array<string> {
    if (permissionLevel === PermissionLevel.EMPLOYEE) {
      return ['/waiter'];
    } else {
      if (!!this.redirectTo) {
        return [this.redirectTo];
      } else {
        return ['/dashboard'];
      }
    }
  }

  private clearCookies(): void {
    this.cookies.remove('email');
    this.cookies.remove('company');
    this.cookies.remove('companyInf');
    this.cookies.remove('country');
    this.cookies.remove('companyObject');
    this.cookies.remove('openingstime');
    this.cookies.remove('closingtime');
    this.cookies.remove('vatInclusive');
    this.cookies.remove('accountText');
    this.cookies.remove('account');
    this.cookies.remove('lastAccountCheck');
    this.cookies.remove('deleteRights');
    this.cookies.remove('loginCount');
    this.cookies.remove('Establishments');
    this.cookies.remove('Companies');
    this.cookies.remove('enforceStock');
    this.cookies.remove('blackbox');
    this.cookies.remove('userRoles');
    this.cookies.remove('betaSettings');
    this.cookies.remove('receiptFilter');
    this.cookies.remove('reportDateRange');
    this.cookies.remove('serverVersion');
    this.cookies.remove('apitoken');
    this.cookies.remove('adminToken');
    this.cookies.remove('selectedEstablishment');
    this.cookies.remove('browserCheck');
  }

  private updateGoogleTagManager(
    account: Account,
    company: CompanySettings,
    user: User
  ): void {
    const pushToDataLayer = (eventName: string) => {
      const dataLayer = window['dataLayer'];
      if (dataLayer) {
        dataLayer.push({
          event: eventName,
          accountCreationDate: account.creationDate.toDate(),
          accountType: account.type,
          companyCity: company.city,
          companyCountry: company.country,
          companyId: company.id,
          companyMasterCompanyId: company.masterCompanyId,
          companyName: company.name,
          companyStreet: company.street,
          companyStreetNumber: company.streetNumber,
          companyTelephone: company.telephone,
          companyTimeZoneId: company.timeZoneId,
          companyZip: company.zip,
          userEmail: user.email,
          userFirstName: user.firstName,
          userLastName: user.lastName,
          userPermissionLevel: user.permissionLevel,
          userType: user.type
        });
      }
    };

    pushToDataLayer('AppMount');

    this.router.events
      .pipe(
        tap((event: RouterEvent) => {
          if (event instanceof NavigationEnd) {
            pushToDataLayer('RouteChange');
          }
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  private setLanguageCode(company: CompanySettings): void {
    if (
      company.languageCode &&
      (window.localStorage.getItem('languageCode') !== company.languageCode ||
        !window.localStorage.getItem('languageCode'))
    ) {
      window.localStorage.setItem(
        'languageCode',
        LocaleUtils.storageToUrlFormat(company.languageCode)
      );
    }
  }
}
